import { Row, Col, Form, Input, Button, Typography, Collapse, Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { TFunction } from 'types';
import { useValidateToken } from './useValidateToken';
import { useSelfRegisterPostResendToken } from 'hooks/useRegistration';
import { useLogin } from 'hooks/useLogin';
import { cookies } from 'static';
import { ApiError, ERROR_TYPES } from 'api/errorTypes';
import { trackAnalyticsEvent, trackLoginSuccess } from 'analytics/ga4';
import { clearAuthSession } from 'utils/clearAuthSession';

const SESSION_KEY_EMAIL = 'lsp_reg_email';
const SESSION_KEY_PASSWORD = 'lsp_reg_password';

const getTrackingErrorType = (error: unknown): string => {
  if (error instanceof ApiError && error.type) {
    return String(error.type).toLowerCase();
  }

  return 'unknown';
};

export default function ValidateToken () {

  const {
    postValidateToken,
    isPostingValidateToken,
  } = useValidateToken();

  const { mutate: postResendToken, isLoading: isPostingResendToken, isSuccess: hasPostedResendToken, isError: failedPostingResendToken } = useSelfRegisterPostResendToken();
  const { mutate: login, isLoading: isLoggingIn, isError: loginFailed } = useLogin();

  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const token = searchParams.get("token");
  const emailFromState = location.state?.email ?? '';
  const passwordFromState = location.state?.password ?? '';

  const emailForLogin = emailFromState || sessionStorage.getItem(SESSION_KEY_EMAIL) || '';
  const passwordForLogin = passwordFromState || sessionStorage.getItem(SESSION_KEY_PASSWORD) || '';

  const [form] = Form.useForm();
  const [resendForm] = Form.useForm();
  const [loginForm] = Form.useForm();
  const { t }: TFunction = useTranslation();
  const [showSuccessScreen, setShowSuccessScreen] = useState(false);
  const autoValidatedTokenRef = useRef<string | null>(null);
  const hasTrackedViewRef = useRef(false);
  const isValidatingToken = isPostingValidateToken;

  const navigateToAppSelection = useCallback((username?: string, authToken?: string) => {
    sessionStorage.removeItem(SESSION_KEY_EMAIL);
    sessionStorage.removeItem(SESSION_KEY_PASSWORD);
    navigate('/app-selection', { replace: true, state: { username, token: authToken, forceIntroBanner: true, showWelcome: true } });
  }, [navigate]);

  const persistAuth = useCallback((authToken?: string, username?: string) => {
    if (!authToken) {
      return;
    }

    localStorage.setItem(cookies.FarmersPlanToken, JSON.stringify({ token: authToken, username: username || emailForLogin }));
  }, [emailForLogin]);

  const redirectToLoginAfterValidation = useCallback((email?: string) => {
    sessionStorage.removeItem(SESSION_KEY_EMAIL);
    sessionStorage.removeItem(SESSION_KEY_PASSWORD);
    clearAuthSession();

    const resolvedEmail = (email || emailForLogin || '').trim();

    navigate('/', {
      replace: true,
      state: {
        emailValidated: true,
        ...(resolvedEmail ? { email: resolvedEmail } : {}),
      },
    });
  }, [emailForLogin, navigate]);

  const completeValidatedRegistration = useCallback((validationData?: any) => {
    const authToken = validationData?.token;
    const username = validationData?.username || validationData?.email || emailForLogin;

    if (authToken) {
      persistAuth(authToken, username);
      void trackLoginSuccess(username);
      navigateToAppSelection(username, authToken);
      return;
    }

    if (emailForLogin && passwordForLogin) {
      trackAnalyticsEvent('reg_flow_validate_login_submit');
      login(
        { username: emailForLogin, password: passwordForLogin },
        {
          onSuccess: (data: any) => {
            trackAnalyticsEvent('reg_flow_validate_login_success');
            persistAuth(data?.token, emailForLogin);
            void trackLoginSuccess(emailForLogin);
            navigateToAppSelection(emailForLogin, data?.token);
          },
          onError: (error: unknown) => {
            trackAnalyticsEvent('reg_flow_validate_login_error', {
              error_type: getTrackingErrorType(error),
            });
            setShowSuccessScreen(true);
          },
        }
      );
      return;
    }

    setShowSuccessScreen(true);
  }, [emailForLogin, passwordForLogin, login, navigateToAppSelection, persistAuth]);

  const handleDuplicateAccountError = useCallback((error: unknown, fromEmailLink = false) => {
    if (error instanceof ApiError && error.type === ERROR_TYPES.DUPLICATE_ACCOUNT) {
      trackAnalyticsEvent('reg_flow_validate_duplicate_account');
      if (fromEmailLink) {
        redirectToLoginAfterValidation();
        return;
      }
      completeValidatedRegistration();
    }
  }, [completeValidatedRegistration, redirectToLoginAfterValidation]);

  useEffect(() => {
    if (hasTrackedViewRef.current) {
      return;
    }

    hasTrackedViewRef.current = true;
    trackAnalyticsEvent('reg_flow_validate_view', {
      has_url_token: Boolean(token?.trim()),
      has_session_email: Boolean(emailForLogin),
    });
  }, [emailForLogin, token]);

  useEffect(() => {
    const urlToken = token?.trim();

    if (!urlToken || autoValidatedTokenRef.current === urlToken) {
      return;
    }

    autoValidatedTokenRef.current = urlToken;
    form.setFieldsValue({ token: urlToken });
    trackAnalyticsEvent('reg_flow_validate_auto_submit');
    postValidateToken(
      { token: urlToken },
      {
        onSuccess: (data: any) => {
          trackAnalyticsEvent('reg_flow_validate_auto_success');
          trackAnalyticsEvent('reg_flow_validate_redirect_login');
          const email = data?.username || data?.email || emailForLogin;
          redirectToLoginAfterValidation(email);
        },
        onError: (error: unknown) => {
          trackAnalyticsEvent('reg_flow_validate_auto_error', {
            error_type: getTrackingErrorType(error),
          });
          handleDuplicateAccountError(error, true);
        },
      }
    );
  }, [emailForLogin, form, handleDuplicateAccountError, postValidateToken, redirectToLoginAfterValidation, token]);

  useEffect(() => {
    const email = emailFromState || emailForLogin;
    if (email) {
      resendForm.setFieldsValue({ registeredEmail: email });
    }
  }, [emailFromState, emailForLogin, resendForm]);

  useEffect(() => {
    if (showSuccessScreen) {
      trackAnalyticsEvent('reg_flow_validate_show_login');
    }
  }, [showSuccessScreen]);

  const onFinish = (values: any) => {
    trackAnalyticsEvent('reg_flow_validate_manual_submit');
    postValidateToken(values, {
      onSuccess: (data: any) => {
        trackAnalyticsEvent('reg_flow_validate_manual_success');
        completeValidatedRegistration(data);
      },
      onError: (error: unknown) => {
        trackAnalyticsEvent('reg_flow_validate_manual_error', {
          error_type: getTrackingErrorType(error),
        });
        handleDuplicateAccountError(error);
      },
    });
  };

  const renderValidateForm = () => {
    return (
      <>
        <Col xs={24} style={{ textAlign: 'center' }}>
          <Typography.Title level={1} style={{ textAlign: 'center' }}>
            {t('Success')} ✅
          </Typography.Title>
          <Typography.Text style={{ textAlign: 'center' }}>
            {t('CheckEmailForValidation')}
          </Typography.Text>
          <Typography.Text style={{ textAlign: 'center', display: 'block', marginTop: 16, color: '#888' }}>
            {t('AddTheVerificationCodeSentToYourEmail')}
          </Typography.Text>
          <Form
            form={form}
            layout="vertical"
            style={{ fontSize: 18, marginTop: 20 }}
            onFinish={onFinish}
            initialValues={{ token }}
            validateTrigger="onChange"
          >
            <Form.Item
              name="token"
              rules={[{ required: true, message: t('TokenIsRequired') }]}
            >
              <Input placeholder={t('Code')} size="large" />
            </Form.Item>
            <Form.Item>
              <Button
                disabled={isValidatingToken}
                loading={isValidatingToken}
                type="primary"
                size="large"
                style={{ width: '100%', fontSize: 14, backgroundColor: '#ff6b3d', height: 46 }}
                onClick={async (e) => { e.preventDefault(); form.submit(); }}
              >
                {t('FinishRegistration')}
              </Button>
            </Form.Item>
          </Form>
        </Col>
        <Col xs={24} style={{ paddingTop: 20 }}>
          <Collapse>
            <Collapse.Panel header={t('DidYouNotReceiveEmail?')} key="1">
              <Form
                form={resendForm}
                layout="vertical"
                onFinish={(values) => {
                  trackAnalyticsEvent('reg_flow_resend_submit');
                  postResendToken(values, {
                    onSuccess: () => {
                      trackAnalyticsEvent('reg_flow_resend_success');
                    },
                    onError: (error: unknown) => {
                      trackAnalyticsEvent('reg_flow_resend_error', {
                        error_type: getTrackingErrorType(error),
                      });
                    },
                  });
                }}
                style={{ fontSize: 18 }}
              >
                <Form.Item name="registeredEmail">
                  <Input size="large" placeholder={t('Email')} />
                </Form.Item>
                <Form.Item>
                  <Button
                    disabled={(isPostingResendToken || hasPostedResendToken) && !failedPostingResendToken}
                    type="primary"
                    size="large"
                    style={{ width: '100%', fontSize: 14, backgroundColor: '#ff6b3d', height: 46 }}
                    onClick={async (e) => { e.preventDefault(); resendForm.submit(); }}
                  >
                    {(isPostingResendToken || hasPostedResendToken) && !failedPostingResendToken ? t('Sent') : t('SendAgain')}
                  </Button>
                </Form.Item>
              </Form>
              <Typography.Text style={{ textAlign: 'center', display: 'flex', justifyContent: 'center' }}>
                <a href="mailto:support@farmersplan.com">{t('ContactSupport')}</a>
              </Typography.Text>
            </Collapse.Panel>
          </Collapse>
        </Col>
      </>
    );
  };

  const renderSuccessScreen = () => {
    if (isLoggingIn) {
      return (
        <Col xs={24} style={{ display: 'flex', justifyContent: 'center', paddingTop: 40 }}>
          <Spin indicator={<LoadingOutlined style={{ fontSize: 40 }} spin />} />
        </Col>
      );
    }
    return (
      <Col xs={24}>
        <Typography.Title level={1} style={{ textAlign: 'center' }}>
          {t('Done')} 🎉
        </Typography.Title>
        <Typography.Text style={{ display: 'block', textAlign: 'center', marginBottom: 20 }}>
          {t('EnterPasswordToAppSelection')}
        </Typography.Text>
        {loginFailed && (
          <Typography.Text style={{ display: 'block', textAlign: 'center', marginBottom: 16, color: '#ff4d4f' }}>
            {t('LoginFailedCheckPassword')}
          </Typography.Text>
        )}
        <Form
          form={loginForm}
          layout="vertical"
          onFinish={(values) => {
            trackAnalyticsEvent('reg_flow_validate_login_submit');
            login(
              { username: emailForLogin, password: values.password },
              {
                onSuccess: (data: any) => {
                  trackAnalyticsEvent('reg_flow_validate_login_success');
                  persistAuth(data?.token, emailForLogin);
                  void trackLoginSuccess(emailForLogin);
                  navigateToAppSelection(emailForLogin, data?.token);
                },
                onError: (error: unknown) => {
                  trackAnalyticsEvent('reg_flow_validate_login_error', {
                    error_type: getTrackingErrorType(error),
                  });
                },
              }
            );
          }}
        >
          <Form.Item name="email" initialValue={emailForLogin}>
            <Input disabled size="large" />
          </Form.Item>
          <Form.Item
            name="password"
            rules={[{ required: true, message: t('PasswordIsRequired') }]}
          >
            <Input.Password placeholder={t('Password')} size="large" style={{ padding: '0px 10px 0px 0px' }} />
          </Form.Item>
          <Form.Item>
            <Button
              type="primary"
              size="large"
              htmlType="submit"
              loading={isLoggingIn}
              style={{ width: '100%', fontSize: 14, backgroundColor: '#ff6b3d', height: 46 }}
            >
              {t('ContinueToAppSelection')}
            </Button>
          </Form.Item>
        </Form>
      </Col>
    );
  };

  if (token?.trim() && isValidatingToken) {
    return (
      <Row>
        <Col xs={24} style={{ display: 'flex', justifyContent: 'center', paddingTop: 40 }}>
          <Spin indicator={<LoadingOutlined style={{ fontSize: 40 }} spin />} />
        </Col>
      </Row>
    );
  }

  return (
    <Row>
      {showSuccessScreen ? renderSuccessScreen() : renderValidateForm()}
    </Row>
  );
}
